/**
 * 模块: BrandCompetition数据管理Hook
 * 职责: 管理品牌竞争数据加载和状态
 * 限制: 文件<280行, 函数<30行
 */

import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import axios from 'axios';
import type { SKU, Period, NetworkData } from './types';

export const useBrandCompetition = () => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [skus, setSkus] = useState<SKU[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedSKU, setSelectedSKU] = useState<number | undefined>();
  const [selectedPeriod, setSelectedPeriod] = useState<number | undefined>();
  const [minFrequency, setMinFrequency] = useState(1);
  const [networkData, setNetworkData] = useState<NetworkData | null>(null);

  const fetchSKUs = useCallback(async () => {
    try {
      const response = await axios.get('/api/skus/');
      setSkus(response.data);
      if (response.data.length > 0) {
        setSelectedSKU(response.data[0].id);
      }
    } catch (error) {
      console.error('获取SKU列表失败:', error);
      message.error('获取SKU列表失败');
    }
  }, []);

  const fetchPeriods = useCallback(async (skuId: number) => {
    try {
      const response = await axios.get(`/api/skus/${skuId}/periods/`);
      setPeriods(response.data);
    } catch (error) {
      console.error('获取周期列表失败:', error);
    }
  }, []);

  const fetchNetworkData = useCallback(async () => {
    if (!selectedSKU) return;

    setLoading(true);
    try {
      const params: any = {
        sku_id: selectedSKU,
        min_frequency: minFrequency,
      };
      if (selectedPeriod) {
        params.period_id = selectedPeriod;
      }

      const response = await axios.get('/api/brand-competitions/network_data/', { params });
      console.log('品牌竞争数据:', {
        节点数: response.data.nodes?.length || 0,
        边数: response.data.edges?.length || 0,
        统计: response.data.stats
      });
      
      setNetworkData(response.data);
      
      if (response.data.nodes?.length > 0 && (!response.data.edges || response.data.edges.length === 0)) {
        message.warning('有品牌数据但无竞争关系，请尝试降低最小竞争频率或重新生成网络');
      }
    } catch (error) {
      console.error('获取网络数据失败:', error);
      message.error('获取网络数据失败');
    } finally {
      setLoading(false);
    }
  }, [selectedSKU, selectedPeriod, minFrequency]);

  const handleGenerateNetwork = useCallback(async () => {
    if (!selectedSKU) {
      message.warning('请先选择SKU');
      return;
    }

    setGenerating(true);
    try {
      const data: any = { sku_id: selectedSKU };
      if (selectedPeriod) {
        data.period_ids = [selectedPeriod];
      }

      await axios.post('/api/brand-competitions/generate_network/', data);
      message.success('品牌竞争网络生成任务已提交，请稍候刷新页面查看结果');
      
      setTimeout(() => {
        fetchNetworkData();
      }, 3000);
    } catch (error) {
      console.error('生成网络失败:', error);
      message.error('生成网络失败');
    } finally {
      setGenerating(false);
    }
  }, [selectedSKU, selectedPeriod, fetchNetworkData]);

  useEffect(() => {
    fetchSKUs();
  }, [fetchSKUs]);

  useEffect(() => {
    if (selectedSKU) {
      fetchPeriods(selectedSKU);
      fetchNetworkData();
    }
  }, [selectedSKU, fetchPeriods, fetchNetworkData]);

  useEffect(() => {
    if (selectedSKU) {
      fetchNetworkData();
    }
  }, [selectedSKU, selectedPeriod, minFrequency, fetchNetworkData]);

  return {
    loading,
    generating,
    skus,
    periods,
    selectedSKU,
    setSelectedSKU,
    selectedPeriod,
    setSelectedPeriod,
    minFrequency,
    setMinFrequency,
    networkData,
    fetchNetworkData,
    handleGenerateNetwork,
  };
};

